<template>
  <div class="ai-container">
    <!-- AI解读整体健康状况 -->
    <div class="ai-interpretation-title">
      <div class="title-with-icon">
        <h2><span class="ai-nutritionist">{{ $t('aiInterpret.titleHighlight') }}</span>{{ $t('aiInterpret.titleSuffix') }}</h2>
        <img
          :src="
            require(`@/assets/AIUnscramble/${
              soundOn === 1 ? 'pause.png' : 'play.png'
            }`)
          "
          :class="['title-volume-icon', { disabled: aiInterpretLoading || !aiInterpretContent }]"
          :alt="$t('common.volume')"
          @click="togglePlayPause"
        />
      </div>
    </div>

    <div class="ai-interpretation-box">
      <div class="ai-interpretation-header">
        <div class="ai-robot">
          <img
            :src="require('@/assets/AIUnscramble/图标-AI小助手@2x.png')"
            :alt="$t('common.robot')"
          />
        </div>
        <div class="interpretation-title">
          <p v-if="aiInterpretLoading || !aiInterpretSummary">
            <span v-if="aiInterpretLoading">{{ $t('aiInterpret.analyzing') }}</span>
            <span v-if="!aiInterpretLoading && !aiInterpretSummary"
              >{{ $t('aiInterpret.generating') }}</span
            >
            <span class="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </p>
          <p v-else v-html="aiInterpretSummary"></p>
        </div>
      </div>
      <div class="interpretation-body" v-if="!isCollapsed">
        <div
          v-if="aiInterpretLoading"
          style="display: none"
          class="loading-container"
        >
          <!-- 已将loading-dots移动到文本后面，这里隐藏原来的容器 -->
        </div>
        <template v-else>
          <p style="white-space: pre-line" v-html="aiInterpretContent"></p>
        </template>
      </div>
    </div>

    <!-- 展开状态下显示所有内容 -->
    <transition name="collapse-transition">
      <div v-if="!isCollapsed">
        <template v-if="showKeywordSections">
        <div class="key-results-title">
          <h3>{{ $t('aiInterpret.keyResults') }}</h3>
        </div>

        <div v-if="keyResultsLoading" class="loading-container">
          <div class="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>

        <template v-else-if="keyResultsData && keyResultsData.length > 0">
          <!-- 重要提示 -->
          <div class="note-content">
            <div class="note-title">
              <img class="note-icon" :src="getIconByLevel('warning')" />
              <div class="title-text">{{ $t('aiInterpret.importantTip') }}</div>
            </div>
            <div class="note-tip">
              {{ $t('aiInterpret.multipleDeficiency') }}
            </div>
          </div>
          <!-- 显示获取到的关键监测结果 -->
          <div
            v-for="(result, index) in keyResultsData"
            :key="index"
            class="nutrient-deficiency-card"
          >
            <div class="deficiency-icon">
              <img :src="getIconByLevel(result.level)" :alt="result.level" />
            </div>
            <div class="deficiency-content">
              <div class="deficiency-title">
                {{ result.title }}
              </div>
              <div class="deficiency-level">
                {{ result.description }}
              </div>
              <div class="deficiency-impact" v-if="result.impact">
                {{ $t('common.impact') }}：{{ result.impact }}
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <!-- 空状态 -->
          <div class="empty-state">{{ $t('aiInterpret.noKeyResults') }}</div>
        </template>

        <div class="affected-organs-title">
          <h3>{{ $t('aiInterpret.affectedConstitution') }}</h3>
        </div>

        <!-- 加载状态 -->
        <div v-if="keyResultsLoading" class="loading-container">
          <div class="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>

        <!-- 有数据时显示器官卡片列表 -->
        <template v-else>
          <div
            class="organ-card"
            v-for="(organ, index) in affectedOrgans"
            :key="organ.system_code + '-' + index"
            @click="handleItemClick(organ)"
          >
            <div class="organ-header">
              <div class="organ-icon">
                <img
                  :src="getOrganIcon(organ.name)"
                  :alt="organ.name + ' ' + $t('common.icon')"
                />
              </div>
              <div class="organ-content">
                <div class="organ-title">{{ organ.name }}</div>
                <!-- <div class="organ-nutrient" v-if="organ.nutrientName">
                  {{ organ.nutrientName }}建议关注项
                </div> -->
              </div>
              <div class="organ-arrow">›</div>
            </div>
            <div class="organ-desc">{{ organ.desc }}</div>
          </div>
        </template>
        </template>
      </div>
    </transition>

    <div class="collapse-button" @click="toggleCollapse">
      <span>{{ isCollapsed ? $t('actions.expandMore') : $t('actions.collapse') }}</span>
      <img
        :src="
          require(`@/assets/AIUnscramble/${
            isCollapsed ? 'Down.png' : 'up.png'
          }`)
        "
        class="collapse-arrow-icon"
        :alt="isCollapsed ? $t('actions.expandMore') : $t('actions.collapse')"
      />
    </div>
    <slot></slot>
  </div>
</template>

<script>
import { getReportKeywordData, getReportStream } from "@/net/api";
import { AudioStreamPlayer } from "@/utils/AudioStreamPlayer.js";

export default {
  name: "AIInterpret",
  data() {
    return {
      // AI解读音量控制
      soundOn: 2,

      // AI解读整体健康状况数据
      aiInterpretLoading: true,
      aiInterpretSummary: "",
      aiInterpretContent: "", // 文本输出
      // 控制内容是否收起，默认展开
      isCollapsed: true,
      // 关键监测结果数据
      keyResultsData: [],
      keyResultsLoading: true,
      // 音频播放器
      audioPlayer: null,
      audioDataBuffer: [],
      isAudioReady: false,
      isUserPlaying: false,
      playedAudioIndex: 0,
      isCollecting: false,
      streamReader: null,
      showKeywordSections: false,
      // 打字机效果
      typewriterQueue: [],
      isTyping: false
    };
  },
  props: {
    // 编号
    packageNo: {
      type: String,
      default: "",
    },
    rand: {
      type: String,
      default: "",
    },
    checkcode: {
      type: String,
      default: "",
    },
  },
  watch: {
    "$store.getters.GetOpenAiChat": {
      handler(newVal) {
        if (newVal) {
          this.leavePage();
        }
      },
      immediate: true, // 可选：是否在初始化时立即执行一次
    },
  },
  methods: {
    // 获取关键监测结果数据
    fetchKeywordData() {
      this.keyResultsLoading = true;
      // 使用当前报告的serial_number作为参数
      const serialNumber = this.packageNo;
      console.log("开始获取关键监测结果数据，参数:", {
        serial_number: serialNumber,
      });

      // 重置数据，避免显示旧数据
      this.keyResultsData = [];
      this.affectedOrgans = [];

      getReportKeywordData({ serial_number: this.packageNo })
        .then((data) => {
          console.log("获取关键监测结果数据成功:", data);

          // 处理关键监测结果数据 (hint)
          if (data && data.hint && data.hint.length > 0) {
            // 转换API返回的hint数据为我们需要的关键监测结果格式
            this.keyResultsData = data.hint.map((item) => {
              // 根据value值判断警告级别
              const value = parseFloat(item.value);


              return {
                title: this.normalizeComplianceText(`${item.name} ${item.lack_degree}`),
                description: this.normalizeComplianceText(`${this.$t('aiInterpret.currentLevel')}: ${item.value}（${this.$t('aiInterpret.normalRange')}: ${item.range[0]}~${item.range[1]}）`),
                impact: this.normalizeComplianceText(item.influence),
                level: value < -1.5 ? 'danger':'notice',
                code: item.system_code,
                name: item.name,
              };
            });
          }

          // 处理可能受影响的身体体质数据 (organs_hint)
          if (data && data.organs_hint && data.organs_hint.length > 0) {
            console.log("处理身体体质数据，数量:", data.organs_hint.length);

            // 确保每个对象都有唯一标识和必要的属性
            this.affectedOrgans = data.organs_hint.map((organ, index) => {
              return {
                name: this.normalizeComplianceText(organ.name || "未知体质"),
                value: organ.value || "",
                desc: this.normalizeComplianceText(organ.influence || ""),
                code: organ.system_code || `unknown-${index}`,
                system_code: organ.system_code || `unknown-${index}`, // 添加system_code字段用作唯一key
                nutrientName: organ.nutrients_name || "",
                index: index, // 添加索引作为备用唯一标识
              };
            });

            console.log("处理后的身体体质数据:", this.affectedOrgans);
          } else {
            console.log("未获取到可能受影响的身体体质数据");
          }

          this.keyResultsLoading = false;
        })
        .catch((err) => {
          console.error("获取关键监测结果数据出错:", err);
          this.keyResultsLoading = false;
        });
    },
    // 根据结果级别获取对应的图标
    getIconByLevel(level) {
      const iconMap = {
        warning: require("@/assets/AIUnscramble/Tips-one (提示)@2x.png"),
        danger: require("@/assets/AIUnscramble/Alarm (警报)@2x.png"),
        notice: require("@/assets/AIUnscramble/Attention (注意)@2x.png"),
      };

      return iconMap[level] || iconMap.notice;
    },
    normalizeComplianceText(text) {
      if (!text) {
        return "";
      }
      return String(text)
        .replace(/严重缺乏/g, "建议关注项")
        .replace(/功能不足/g, "相关状态建议关注")
        .replace(/供血压力增大/g, "建议结合饮食、作息、运动综合管理")
        .replace(/供血不足/g, "建议结合饮食、作息、运动综合管理");
    },
    // 收起/展开功能
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },
    // 获取器官对应的图标
    getOrganIcon(organName) {
      // 从affectedOrgans中找到对应的器官数据
      const organ = this.affectedOrgans.find(item => item.name === organName);
      const systemCode = organ ? organ.system_code : null;
      
      // 如果有system_code，直接使用它构建图片路径
      if (systemCode) {
        return `/assets/bodyImg/${systemCode}.png`;
      }
      
      // 返回默认图标
      return "/assets/bodyImg/A003.png";
    },
    // 离开页面，暂停音频
    leavePage() {
      if (this.audioPlayer && this.soundOn === 1) {
        this.audioPlayer.pause();
        this.soundOn = 2;
        this.isUserPlaying = false;
        this.$store.commit("SetOpenInterpret", { openInterpret: false });
      }
    },
    // 可能受影响的身体体质跳转到详情页
    handleItemClick(item) {
      this.$router.push({
        path: "/details",
        query: {
          av: item.value || "",
          code: item.system_code || "",
          title: item.name || "",
          color: item.color || "",
          packageNo: this.packageNo,
          rand: this.rand,
          checkcode: this.checkcode,
          lang: this.$route.query.lang
        },
      });
    },

    async togglePlayPause() {
      // 如果正在加载或没有内容，则不允许播放
      if (this.aiInterpretLoading || !this.aiInterpretContent) {
        return;
      }
      
      if (!this.audioPlayer) {
        this.initAudioPlayer();
        await this.$nextTick();
      }
      
      if (this.soundOn === 2) {
        // 开始播放
        this.isUserPlaying = true;
        this.soundOn = 1;
        this.$store.commit("SetOpenInterpret", { openInterpret: true });
        
        // 播放所有已缓存的音频（从未播放的部分开始）
        this.playBufferedAudio();
        
        // 只有在有音频数据时才恢复播放器
        if (this.audioPlayer.hasAudioData()) {
          this.audioPlayer.resume();
        }
      } else {
        // 暂停播放
        this.isUserPlaying = false;
        this.soundOn = 2;
        this.$store.commit("SetOpenInterpret", { openInterpret: false });
        
        // 暂停音频播放器
        this.audioPlayer.pause();
      }
    },



    // 初始化音频播放器
    initAudioPlayer() {
      this.audioPlayer = new AudioStreamPlayer({
        sampleRate: 16000,
        channels: 1,
        volume: 1,
        onStatusUpdate: (status) => {
          console.log('音频状态:', status);
        },
        onPlaybackComplete: () => {
          this.isUserPlaying = false;
          this.soundOn = 2;
          this.$store.commit("SetOpenInterpret", { openInterpret: false });
        }
      });
      
      // 默认不播放，等待用户点击
      this.isUserPlaying = false;
      this.soundOn = 2;
      
      // 确保音频播放器初始化为暂停状态
      if (this.audioPlayer && this.audioPlayer.pause) {
        this.audioPlayer.pause();
      }
    },

    async startInterpretation() {
      // 音频播放器已在mounted中初始化
      
      const response = await getReportStream({
        serial_number: this.packageNo,
      });
      
      this.streamReader = response.body.getReader();
      const decoder = new TextDecoder();
      let partialData = "";
      this.isCollecting = true;
      
      // 初始化打字机效果
      this.typewriterQueue = [];
      this.isTyping = false;
      
      while (this.isCollecting) {
        const { done, value } = await this.streamReader.read();
        if (done) break;
        
        const textData = decoder.decode(value, { stream: true });
        const dataChunks = (partialData + textData).split("\n");
        partialData = dataChunks.pop() || "";
        
        for (const chunk of dataChunks) {
          if (!chunk.trim()) continue;
          
          let jsonString = chunk;
          if (chunk.startsWith("data: ")) {
            jsonString = chunk.substring(6);
          }
          
          try {
            const jsonData = JSON.parse(jsonString);
            const { token, summarize, audio, token_finish } = jsonData;
            
            if (token) {
              this.addTokenWithTypewriter(token);
            }
            
            if (summarize) {
              this.aiInterpretSummary += summarize;
            }
            
            if (audio) {
              this.audioDataBuffer.push(audio);
              this.isAudioReady = true;
              
              // 始终缓存音频数据，但只有在用户允许时才播放
              if (this.isUserPlaying) {
                if (!this.audioPlayer) {
                  this.initAudioPlayer();
                }
                this.audioPlayer.playBase64Audio(audio);
                this.playedAudioIndex = this.audioDataBuffer.length;
                
                // 确保播放器处于播放状态
                if (this.audioPlayer.isPaused) {
                  this.audioPlayer.resume();
                }
              }
            }
            
            if (token_finish) {
              this.aiInterpretLoading = false;
              this.$emit("suggestionHandle");
              this.$store.commit("SetAiChatEnabled", {
                aiChatEnabled: true,
              });
            }
          } catch (e) {
            console.error("解析数据失败:", e);
          }
        }
      }
    },


    // 添加token并使用打字机效果
    addTokenWithTypewriter(token) {
      this.typewriterQueue.push(token);
      
      if (!this.isTyping) {
        this.processTypewriterQueue();
      }
    },
    
    // 处理打字机队列
    processTypewriterQueue() {
      if (this.typewriterQueue.length === 0) {
        this.isTyping = false;
        return;
      }
      
      this.isTyping = true;
      const token = this.typewriterQueue.shift();
      
      // 逐字显示
      let charIndex = 0;
      const typeInterval = setInterval(() => {
        if (charIndex < token.length) {
          this.aiInterpretContent += token[charIndex];
          charIndex++;
        } else {
          clearInterval(typeInterval);
          setTimeout(() => {
            this.processTypewriterQueue();
          }, 10);
        }
      }, 30); // 每30ms显示一个字符
    },
    
    // 播放缓冲的音频
    playBufferedAudio() {
      if (this.audioDataBuffer.length === 0) return;
      
      for (let i = this.playedAudioIndex; i < this.audioDataBuffer.length; i++) {
        this.audioPlayer.playBase64Audio(this.audioDataBuffer[i]);
      }
      this.playedAudioIndex = this.audioDataBuffer.length;
    },
    
    // 开始音频播放
    startAudioPlayback() {
      this.isUserPlaying = true;
      this.$store.commit("SetOpenInterpret", { openInterpret: true });
      this.playBufferedAudio();
    },

    // 暂停音频
    stopPlay() {
      if (this.audioPlayer) {
        this.audioPlayer.pause();
        this.soundOn = 2;
        this.isUserPlaying = false;
        this.$store.commit("SetOpenInterpret", { openInterpret: false });
      }
    },


  },

  mounted() {
    // 立即初始化音频播放器
    this.initAudioPlayer();
    this.startInterpretation();
    if (this.showKeywordSections) {
      this.fetchKeywordData();
    }
  },
  deactivated() {
    this.leavePage();
  },

  beforeUnmount() {
    this.isCollecting = false;
    if (this.streamReader) {
      this.streamReader.cancel();
      this.streamReader = null;
    }
    if (this.audioPlayer) {
      this.audioPlayer.stop();
      this.audioPlayer = null;
    }
    // 清理打字机效果
    this.typewriterQueue = [];
    this.isTyping = false;
    this.isUserPlaying = false;
  },
};
</script>

<style lang="scss" scoped>
.ai-container {
  padding: 0 26px;
  //  background-color: pink;
}
/* AI解读整体健康状况 */
.ai-interpretation-title {
  display: flex;
  padding: 20px 15px 15px;
}

.title-with-icon {
  display: flex;
  align-items: center;
}

.ai-interpretation-title h2 {
  font-size: 32px;
  font-weight: 600;
  color: #2c324e;
  margin: 0;
  margin-right: 10px;
}

.ai-nutritionist {
  color: #003CBF;
}

.title-volume-icon {
  width: 38px;
  height: 38px;
  margin-left: 20px;
  cursor: pointer;
  transition: opacity 0.3s ease;
}

.title-volume-icon.disabled {
  opacity: 0.3;
  cursor: not-allowed;
  pointer-events: none;
}

.ai-interpretation-box {
  // background: #FFFFFF;
  border-radius: 20px;
  border: 2px solid #cecece;
  margin: 0 15px 20px;
  padding: 20px;
}

.ai-interpretation-header {
  display: flex;
  align-items: center;
  margin-top: 25px;
  margin-bottom: 20px;
  padding-left: 20px;
}

.ai-robot {
  margin-right: 15px;
}

.ai-robot img {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  object-fit: contain;
}

.interpretation-title {
  flex: 1;
  padding-left: 20px;
}

.interpretation-title p {
  font-size: 24px;
  font-weight: 500;
  color: #808080;
  margin: 0;
  line-height: 1.4;
}

.interpretation-body {
  padding-top: 10px;
}

.interpretation-body p {
  font-size: 26px;
  line-height: 1.6;
  color: #495880;
  margin-bottom: 15px;
  font-weight: 500;
}

.interpretation-body p:last-child {
  margin-bottom: 0;
}

.key-results-title,
.affected-functions-title {
  padding: 20px 15px 10px;
  font-size: 15px;
}

.key-results-title h3,
.affected-functions-title h3 {
  font-size: 32px;
  font-weight: 600;
  color: #333;
  margin: 0;
}
// 重要提示
.note-content {
  width: 90%;
  background-color: #f0f0f0;
  font-size: 28px;
  padding: 30px;
  border-radius: 20px;
  margin: 20px auto;
  // display: flex;
  .note-icon {
    width: 30px;
    height: 30px;
    margin-right: 20px;
  }
  .note-title {
    display: flex;
    align-items: center;
    font-weight: bold;
  }
  .note-tip {
    font-size: 24px;
    color: #495880;
    margin-left: 50px;
    padding-top: 10px;
  }
}

/* 营养素缺乏卡片样式 - 根据用户提供的图片样式 */
.nutrient-deficiency-card {
  width: 90%;
  margin: 20px auto;

  display: flex;
  align-items: flex-start;
  background: #fefaf9;
  border: 2px solid #ffdede;
  border-radius: 20px;
  margin: 0 15px 25px;
  padding: 20px;
  // box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.deficiency-icon {
  margin-right: 15px;
  flex-shrink: 0;
}

.deficiency-icon img {
  width: 32px;
  height: 32px;
}

.deficiency-content {
  flex: 1;
}

.deficiency-title {
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #2c324e;
}

.deficiency-level {
  font-size: 26px;
  color: #495880;
  margin-bottom: 12px;
}

.deficiency-impact {
  font-size: 26px;
  color: #495880;
  line-height: 1.5;
  white-space: normal;
  word-break: break-all;
}

/* 保留原有样式用于其他部分 */
.result-card {
  display: flex;
  align-items: flex-start;
  background: #ffffff;
  border-radius: 12px;
  margin: 0 15px 30px;
  padding: 18px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.warning-card {
  background-color: #ececec;
}

.affected-functions {
  padding: 20px;
}

.affected-functions h3 {
  font-size: 30px;
  font-weight: 600;
  color: #333;
  margin: 0 0 15px;
}

.function-item {
  display: flex;
  padding: 15px 0;
  border-bottom: 1px solid #eaedf2;
  align-items: center;
}

.function-icon {
  width: 60px;
  height: 60px;
  margin-right: 15px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.function-icon img {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.function-text-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  font-size: 22px;
  font-weight: 500;
  color: #003cbf;
  background-color: #e9f0ff;
  border-radius: 50%;
}

.function-details {
  flex: 1;
}

.function-title {
  font-size: 28px;
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
}

.function-desc {
  font-size: 24px;
  color: #666;
}

.function-arrow {
  font-size: 30px;
  color: #999;
  margin-left: 10px;
}

/* 可能受影响的器官功能 */
.affected-organs-title {
  padding: 20px 15px 10px;
}

.affected-organs-title h3 {
  font-size: 32px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.organ-card {
  display: flex;
  flex-direction: column;
  background: #fefaf9;
  border-radius: 16px;
  margin: 0 20px 20px;
  padding: 20px;
  // box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #eaeaea;
  // cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid #ffdede;
}

.organ-card:hover {
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.organ-header {
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: flex-start;
  margin-bottom: 8px;
}

.organ-content {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.organ-icon {
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.organ-icon img {
  width: 45px;
  height: 45px;
  object-fit: contain;
}

.organ-title {
  font-size: 28px;
  font-weight: 500;
  color: #2c324e;
  flex: 1;
}

.organ-nutrient {
  font-size: 24px;
  color: #6c7ab4;
  margin-top: 4px;
  margin-bottom: 6px;
}

.organ-desc {
  font-size: 24px;
  color: #495880;
  line-height: 1.4;
  // padding-left: 0;
  margin-top: 5px;
  margin-left: 10px;
}

.organ-arrow {
  font-size: 46px;
  color: #999;
  margin-right: 30px;
  flex-shrink: 0;
}

.collapse-button {
  text-align: center;
  padding: 15px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  // cursor: pointer;
  margin: 5px 0;
  font-size: 28px;
}

.collapse-arrow-icon {
  width: 40px;
  height: 40px;
  margin-left: 10px;
  transition: transform 0.3s;
}

.collapse-icon.is-expanded {
  transform: rotate(0deg);
}

.chat-button-container {
  padding: 20px 15px 25px;
}

.ai-chat-button {
  width: 100%;
  height: 90px;
  background-color: #003cbf;
  color: #ffffff;
  border: none;
  border-radius: 45px;
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  // cursor: pointer;
}
/* 空状态样式 */
.empty-state {
  text-align: center;
  padding: 40px 0;
  color: #999;
  font-size: 28px;
}

.primary-text {
  font-size: 28px;
  font-weight: 500;
  margin-bottom: 5px;
}

.secondary-text {
  font-size: 22px;
  font-weight: 400;
  opacity: 0.9;
}

/* 添加折叠/展开动画 */
.collapse-transition-enter-active,
.collapse-transition-leave-active {
  transition: all 0.3s ease;
  max-height: 1000px; /* 足够大的高度以确保内容完全展示 */
  overflow: hidden;
}

.collapse-transition-enter-from,
.collapse-transition-leave-to {
  max-height: 0;
  opacity: 0;
  margin-top: 0;
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
}

/* 加载动画样式 */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
}

.loading-dots {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin-left: 5px;
}

.loading-dots span {
  display: inline-block;
  width: 10px;
  height: 10px;
  margin: 0 3px;
  background-color: #003cbf;
  border-radius: 50%;
  opacity: 0.6;
  animation: loadingDots 1.4s infinite ease-in-out both;
}

.loading-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes loadingDots {
  0%,
  80%,
  100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

/* 添加流式文字展示的动画效果 */
.typing-animation {
  position: relative;
  border-right: 2px solid transparent;
  animation: typing-cursor 0.8s infinite;
}

@keyframes typing-cursor {
  0%,
  100% {
    border-color: transparent;
  }
  50% {
    border-color: #333;
  }
}
</style>
